import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';

function emptyForm() {
  return { id: null, name: '', destination: '', inclusions: [''], exclusions: [''] };
}

export default function PackageOptions() {
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'system';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const lineInputRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('package_option_templates').select('*').order('name');
    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setLine = (key, i, value) => setForm(f => ({ ...f, [key]: f[key].map((x, idx) => idx === i ? value : x) }));
  const removeLine = (key, i) => setForm(f => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  // Adds a new blank line and focuses it — lets staff enter a whole list of items by
  // pressing Enter repeatedly, without reaching for the "+ Add Line" button each time.
  const addLine = (key) => {
    setForm(f => {
      const next = { ...f, [key]: [...f[key], ''] };
      const newIndex = next[key].length - 1;
      setTimeout(() => lineInputRefs.current[`${key}-${newIndex}`]?.focus(), 0);
      return next;
    });
  };

  function editTemplate(row) {
    setForm({
      id: row.id,
      name: row.name || '',
      destination: row.destination || '',
      inclusions: row.inclusions?.length ? row.inclusions : [''],
      exclusions: row.exclusions?.length ? row.exclusions : [''],
    });
    setError('');
  }

  async function saveTemplate() {
    if (!form.name.trim()) { setError('Enter a package name.'); return; }
    setSaving(true); setError('');
    const clean = {
      name: form.name.trim(),
      destination: form.destination.trim() || null,
      inclusions: form.inclusions.map(x => x.trim()).filter(Boolean),
      exclusions: form.exclusions.map(x => x.trim()).filter(Boolean),
    };
    let err;
    if (form.id) {
      ({ error: err } = await supabase.from('package_option_templates').update({ ...clean, updated_at: new Date().toISOString() }).eq('id', form.id));
    } else {
      ({ error: err } = await supabase.from('package_option_templates').insert({ ...clean, created_by: username }));
    }
    setSaving(false);
    if (err) { setError(err.message); return; }
    setForm(emptyForm());
    load();
  }

  async function toggleActive(row) {
    await supabase.from('package_option_templates').update({ is_active: !row.is_active }).eq('id', row.id);
    load();
  }

  async function removeTemplate(row) {
    if (!window.confirm(`Remove "${row.name}"? This won't affect quotations that already loaded it.`)) return;
    await supabase.from('package_option_templates').delete().eq('id', row.id);
    if (form.id === row.id) setForm(emptyForm());
    load();
  }

  const renderLineList = (label, key) => (
    <div className="form-group" style={{ marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <label className="form-label" style={{ margin: 0 }}>{label}</label>
        <button type="button" className="btn btn-sm btn-secondary" onClick={() => addLine(key)}>+ Add Line</button>
      </div>
      {form[key].map((line, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          <input
            ref={el => { lineInputRefs.current[`${key}-${i}`] = el; }}
            className="form-input"
            value={line}
            onChange={e => setLine(key, i, e.target.value)}
            onKeyDown={e => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              if (i === form[key].length - 1) addLine(key);
              else lineInputRefs.current[`${key}-${i + 1}`]?.focus();
            }}
            placeholder={`${label.slice(0, -1)} ${i + 1}`}
            style={{ flex: 1 }}
          />
          {form[key].length > 1 && (
            <button type="button" onClick={() => removeLine(key, i)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 18 }}>×</button>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Package Options</div>
          <div className="page-subtitle">Reusable presets (e.g. "Malaysia Package") with typical inclusions/exclusions — staff load one into a Detail Package quotation and tick which items apply, instead of retyping the same list every time</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 12 }}>{form.id ? 'Edit Package Option' : 'New Package Option'}</div>
        {error && <div style={{ color: 'var(--danger)', marginBottom: 10, fontSize: 13 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Package Name *</label>
            <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Malaysia Package" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Destination (optional)</label>
            <input className="form-input" value={form.destination} onChange={e => setForm(f => ({ ...f, destination: e.target.value }))} placeholder="e.g. Malaysia" />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 16 }}>
          {renderLineList('Inclusions', 'inclusions')}
          {renderLineList('Exclusions', 'exclusions')}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" className="btn btn-primary" onClick={saveTemplate} disabled={saving}>{saving ? 'Saving…' : form.id ? 'Save Changes' : '+ Add Package Option'}</button>
          {form.id && <button type="button" className="btn btn-secondary" onClick={() => setForm(emptyForm())}>Cancel Edit</button>}
        </div>
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="card" style={{ padding: 0 }}>
          {rows.length === 0 ? (
            <div style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No package options yet.</div>
          ) : rows.map(row => (
            <div key={row.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', opacity: row.is_active ? 1 : 0.45 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{row.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {row.destination ? `${row.destination} · ` : ''}{(row.inclusions || []).length} inclusion{(row.inclusions || []).length !== 1 ? 's' : ''}, {(row.exclusions || []).length} exclusion{(row.exclusions || []).length !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => editTemplate(row)}>Edit</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => toggleActive(row)}>{row.is_active ? 'Deactivate' : 'Activate'}</button>
                <button type="button" className="btn btn-danger btn-sm" onClick={() => removeTemplate(row)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
